// Topic controls: the global switches, one flat list (the on-by-default
// split was answered once, in onboarding, and is not re-grouped here).
// What-reaches-you-from-one-person lives on the expert profile since
// 28 Jul 2026; it only means anything for someone you follow.
import { useEffect, useState } from "react";
import { AREAS } from "../../shared/areas.js";
import { TOPICS } from "../../shared/ontology.js";
import type { Descriptor } from "../shell/descriptor.js";
import { COMMITMENTS } from "../copy.js";
import { Avatar } from "../shell/Avatar.js";
import { RailCard, useExpert } from "../shell/fragments.js";
import { TopicRow } from "./onboarding.js";
import { useStore, type AppStore } from "../state/store.js";

// Only filterable kinds are user-facing switches; everyday kinds flow.
const FILTERABLE = new Set(TOPICS.filter((t) => t.filterable).map((t) => t.id));

function DeleteAccount() {
  const s = useStore();
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const email = s.user?.email ?? "";
  return (
    <div style={{ marginTop: 10 }}>
      <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6 }}>
        Deleting your account erases every record: follows, practices, history, stances. There is no soft-delete and no shadow copy. Type your email address to confirm.
      </p>
      <input
        className="tin"
        type="email"
        placeholder={email}
        value={confirm}
        aria-label="Type your email to confirm deletion"
        onChange={(e) => setConfirm(e.target.value)}
      />
      {error && (
        <p style={{ fontSize: 12, color: "var(--rose)", marginTop: 6 }}>{error}</p>
      )}
      <button
        className="btn"
        style={{ marginTop: 10 }}
        disabled={confirm.trim().toLowerCase() !== email}
        onClick={() => {
          void (async () => {
            const res = await fetch("/api/account/delete", {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ confirm }),
            });
            if (res.ok) location.href = "/welcome";
            else setError("That did not match. Nothing was deleted.");
          })();
        }}
      >
        Delete my account
      </button>
    </div>
  );
}

// The day turns over at midnight in this zone; it decides when fresh
// suggestions arrive and which day a tick lands on. Automatic follows the
// device at every boot; a picked zone sticks until changed here.
function TimezonePanel() {
  const user = useStore((s) => s.user);
  const setTimezone = useStore((s) => s.setTimezone);
  if (!user) return null;
  const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const zones: string[] =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  if (!zones.includes(user.timezone)) zones.unshift(user.timezone);
  const auto = user.timezoneSource !== "user";
  return (
    <section className="panel glass">
      <span className="u">Timezone</span>
      <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6, margin: "8px 0 4px" }}>
        Your day turns over at midnight here: it sets when new practices arrive and which day a tick counts toward. Automatic follows your device.
      </p>
      <select
        className="tin"
        aria-label="Timezone"
        value={auto ? "auto" : user.timezone}
        onChange={(e) => void setTimezone(e.target.value === "auto" ? null : e.target.value)}
      >
        <option value="auto">Automatic: {deviceTz.replaceAll("_", " ")}</option>
        {zones.map((z) => (
          <option key={z} value={z}>
            {z.replaceAll("_", " ")}
          </option>
        ))}
      </select>
    </section>
  );
}

// Arriving with arg "data" (the See how CTA on My Practice) starts the
// page at the Your data section instead of the top.
function ScrollToData() {
  useEffect(() => {
    document.getElementById("your-data")?.scrollIntoView({ block: "start" });
  }, []);
  return null;
}

export function controls(state: AppStore, arg: string | null = null): Descriptor {
  const s = state.appState;

  if (!s) {
    return {
      nav: "controls",
      title: "Topic controls",
      main: <section className="panel glass"><p className="empty">Loading…</p></section>,
    };
  }

  const mutedRows = s.mutes;
  const hidden = s.discover.hidden.length;
  const filterRows = s.topics.filter((t) => FILTERABLE.has(t.id));
  const onCount = filterRows.filter((t) => t.on).length;

  const main = (
    <>
      <p style={{ fontSize: 13.5, color: "var(--t-mid)", lineHeight: 1.6, maxWidth: "64ch" }}>
        Everything on Practice is available to you. These switches decide what reaches your day. Nothing here is blocked, and where a topic carries a caveat we say what it is.
      </p>

      {/* The step-1 answer stays editable here (28 Jul 2026): these areas
          feed Follow's Recommended order and the onboarding expert step, so
          they are a living setting, not signup history. */}
      <section className="panel glass">
        <span className="u">What you want to work on</span>
        <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6, margin: "8px 0 10px" }}>
          Your areas from signup. They shape who we recommend under Follow and during onboarding; changing them never hides anything.
        </p>
        {/* The same tiles as signup's step 1, compact (29 Jul 2026): one
            answer should look like one answer on both screens, and a row
            of 14 pills read as neither. */}
        <div className="obgrid tight">
          {AREAS.map(([id, label, emoji]) => {
            const on = s.areas.includes(id);
            return (
              <button
                key={id}
                className="goal sm"
                aria-pressed={on}
                onClick={() =>
                  void state.setAreas(on ? s.areas.filter((a) => a !== id) : [...s.areas, id])
                }
              >
                <span className="emo">{emoji}</span>
                <span>{label}</span>
                <span className="ck">✓</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="panel glass">
        <span className="u">Topic filters</span>
        <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6, margin: "8px 0 2px" }}>
          If you would rather not get certain kinds of advice from the people you follow, filter them here. Sensitive topics are filtered unless you switch them on here.
        </p>
        <div style={{ marginTop: 8 }}>
          {s.topics
            .filter((t) => FILTERABLE.has(t.id))
            .map((t) => (
              <TopicRow
                key={t.id}
                t={{ id: t.id, label: t.label, short: t.short, domain: t.domain, defaultOn: t.defaultOn, filterable: true, reason: t.reason }}
                checked={t.on}
                onToggle={() => void state.setTopic(t.id, !t.on)}
              />
            ))}
        </div>
      </section>

      <section className="panel glass">
        {/* "Muted from one person" read as a count next to an empty list;
            the heading names the control, the copy below explains it. */}
        <span className="u">Per-person mutes</span>
        <div style={{ marginTop: 10 }}>
          {mutedRows.length ? (
            mutedRows.map((m) => <MuteRow key={`${m.expertId}:${m.topicId}`} expertId={m.expertId} topicId={m.topicId} />)
          ) : (
            <p className="empty" style={{ padding: "6px 0" }}>
              Nothing muted.
            </p>
          )}
          <p style={{ fontSize: 12, color: "var(--t-low)", lineHeight: 1.55, marginTop: 10 }}>
            A per-expert mute is the distinction this control exists for: you can want someone’s training material without wanting their opinion on everything else. Set one from any expert profile.
          </p>
        </div>
      </section>

      <div className="notice safe">
        <span className="ni">⚖</span>
        <span>
          <b>What we don’t do with these switches.</b> We don’t use them to quietly remove a viewpoint we dislike, and we don’t hide the fact that they’re working. Every screen that drops something because of a setting says so, and links back here.
        </span>
      </div>

      <section className="panel glass">
        <span className="u">What we hold to at every setting</span>
        <div style={{ marginTop: 10 }}>
          {COMMITMENTS.slice(0, 4).map(([title, body]) => (
            <div key={title} style={{ padding: "11px 0", borderTop: "1px solid var(--hair)" }}>
              <b style={{ fontSize: 13.5 }}>{title}</b>
              <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6, marginTop: 5 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      <TimezonePanel />

      <section className="panel glass" id="your-data" style={{ scrollMarginTop: 16 }}>
        {arg === "data" && <ScrollToData />}
        <span className="u">Your data</span>
        <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6, marginTop: 8 }}>
          The record is yours: export everything, or delete everything.
        </p>
        <div className="btnrow" style={{ marginTop: 10 }}>
          <a className="btn" style={{ textDecoration: "none" }} href="/api/account/export" download="practice-export.json">
            Export my data
          </a>
          <button className="btn quiet" onClick={() => void state.signOut()}>
            Sign out
          </button>
        </div>
        <DeleteAccount />
      </section>
    </>
  );

  const rail = (
    <>
      <RailCard eyebrow="Right now" title={`${filterRows.length - onCount} topic${filterRows.length - onCount === 1 ? "" : "s"} filtered`}>
        <p>
          {/* Zero mutes goes unmentioned: "0 per-expert mutes" is a
              non-fact dressed as a status. */}
          {mutedRows.length > 0 &&
            `${mutedRows.length} per-expert mute${mutedRows.length === 1 ? "" : "s"}. `}
          {hidden
            ? `Your settings are currently hiding ${hidden} practice${hidden > 1 ? "s" : ""}, and we list every one.`
            : "Nothing is currently hidden by a setting."}
        </p>
        {hidden > 0 && (
          <button
            className="btn blockbtn"
            onClick={() => {
              state.setDiscTab("hidden");
              state.nav("discover");
            }}
          >
            See what’s hidden ›
          </button>
        )}
      </RailCard>
      <RailCard eyebrow="What never moves" title="Our commitments">
        <p>We will never:</p>
        <ul className="commits">
          {COMMITMENTS.slice(0, 5).map(([title]) => (
            <li key={title}>{title.replace(/^We won’t /, "")}</li>
          ))}
        </ul>
        <p style={{ color: "var(--t-low)" }}>
          These are accuracy and disclosure commitments. They don’t change with your settings.
        </p>
      </RailCard>
    </>
  );

  return { nav: "controls", title: "Topic controls", main, rail };
}

function MuteRow({ expertId, topicId }: { expertId: string; topicId: string }) {
  const s = useStore();
  const e = useExpert(expertId);
  const t = s.appState?.topics.find((x) => x.id === topicId);
  return (
    <div className="lrow">
      <span>
        <Avatar e={e} size="s" />
      </span>
      <span className="grow">
        <strong style={{ fontFamily: "var(--ui)", fontSize: 13.5 }}>{t?.label ?? topicId}</strong>
        <small>from {e.short ?? e.name}, available from everyone else</small>
      </span>
      <button className="btn quiet" onClick={() => void s.setMute(expertId, topicId, false)}>
        Unmute
      </button>
    </div>
  );
}

